import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook simplificado para manejar navegación desde el área de cliente
 * Cuando el usuario está en rutas del área de cliente y presiona retroceder,
 * será dirigido a la página principal
 */
export const useBackToHome = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Rutas donde queremos controlar la navegación hacia atrás
    const clientAreaRoutes = ['/mi-area', '/historial-simulaciones'];
    
    if (clientAreaRoutes.includes(location.pathname)) {
      // Función para manejar el evento popstate (botón retroceder/adelante)
      const handlePopState = (event) => {
        // Prevenir el comportamiento por defecto
        event.preventDefault();
        
        // Navegar a la página principal
        navigate('/', { replace: true });
      };

      // Agregar el listener cuando estamos en una ruta del área de cliente
      window.addEventListener('popstate', handlePopState);

      // También podemos manipular el historial para asegurar el comportamiento
      // Esto hace que cuando el usuario llegue a estas rutas,
      // el historial anterior sea reemplazado por la página principal
      const currentState = window.history.state;
      if (!currentState || !currentState.fromClientArea) {
        // Reemplazar la entrada actual con metadata
        window.history.replaceState(
          { 
            fromClientArea: true,
            originalPath: location.pathname
          }, 
          '', 
          location.pathname
        );
      }

      // Cleanup - remover el listener cuando se desmonte o cambie la ruta
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [location.pathname, navigate]);
};

/**
 * Hook alternativo usando history.pushState para forzar comportamiento
 */
export const useClientAreaNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const clientAreaRoutes = ['/mi-area', '/historial-simulaciones'];
    
    if (clientAreaRoutes.includes(location.pathname)) {
      // Almacenar la función original de history.back
      const originalBack = window.history.back;
      
      // Sobrescribir history.back para ir a la página principal
      window.history.back = () => {
        navigate('/', { replace: true });
      };

      // Sobrescribir también el listener de popstate
      const handlePopState = () => {
        navigate('/', { replace: true });
      };

      window.addEventListener('popstate', handlePopState);

      // Cleanup
      return () => {
        window.history.back = originalBack;
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [location.pathname, navigate]);

  // Función para navegar de forma controlada al área de cliente
  const navigateToClientArea = (route) => {
    navigate(route, { replace: false });
  };

  // Función para navegar de vuelta a la página principal
  const navigateToHome = () => {
    navigate('/', { replace: true });
  };

  return {
    navigateToClientArea,
    navigateToHome
  };
};